import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import PageHeader from "../components/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import PersonAvatar from "../components/people/PersonAvatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { DateField } from "../components/ui/DateField";
import { SearchableCombobox } from "../components/ui/SearchableCombobox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Plus,
  Trash2,
  Paperclip,
  Check,
  X,
  Receipt,
  Clock,
  CheckCircle2,
  IndianRupee,
  RefreshCw,
  Undo2,
  Pencil,
  AlertTriangle,
  Info,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
} from "lucide-react";
import { format, startOfMonth } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import { isAdminRole } from "../utils/rolePermissions";
import ConveyanceNotice from "../components/ConveyanceNotice";
import {
  addExpenseCategory,
  createExpense,
  updateExpense,
  deleteExpense,
  expenseReceiptHref,
  exportExpenseTransferSheet,
  getExpenseCategories,
  getExpenses,
  openExpenseReceipt,
  reimburseExpense,
  retryExpensePush,
  submitDraftExpense,
  updateExpenseStatus,
  uploadReceipt,
  withdrawExpense,
  type ExpenseReport,
  type ExpenseScope,
  type ExpenseLineItem,
} from "../api/expenses";
import { BankExportDialog, transferSheetFilename } from "../components/BankExportDialog";
import { ExpenseStatementUploadDialog } from "../components/ExpenseStatementUploadDialog";
import { vendorsApi, type Vendor } from "../api/vendors";
import { clientsApi, type Client } from "../api/clients";
import { getCompany } from "../api/officeandcompany";

// UI tab keys: the three backend scopes plus a "drafts" view (status=DRAFT).
type ExpenseTab = ExpenseScope | "drafts";

const RECEIPT_THRESHOLD = 500;

// Sentinel an expense carries as its approver when the filer has no manager
// above them: "whoever is HR Admin decides this". Matches HR_ADMIN_APPROVER in
// hrms/utils/expense_notifier.py.
const HR_ADMIN_APPROVER = "hr_admin";

// Today's date in the "yyyy-MM-dd" shape DateField expects.
const todayISO = () => format(new Date(), "yyyy-MM-dd");
// First day of the current month — the default start of the date filter.
const monthStartISO = () => format(startOfMonth(new Date()), "yyyy-MM-dd");

function fmtMoney(n: number | string | null | undefined): string {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return format(new Date(s), "dd MMM yyyy");
  } catch {
    return String(s);
  }
}

// Pull a human-readable string out of an axios error. FastAPI 422s return
// `detail` as an ARRAY of {loc,msg,...} objects (other errors may return an
// object) — never hand those straight to a toast/JSX or React throws
// "Objects are not valid as a React child" and blanks the screen.
function errMsg(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => (x && typeof x === "object" ? x.msg : typeof x === "string" ? x : null))
      .filter(Boolean);
    if (parts.length) return parts.join(", ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  return fallback;
}

const notifySuccess = (message: string, description?: string, id?: any) =>
  toast.success(message, { id, description, duration: 3200 });

const notifyError = (message: string, description?: string, id?: any) =>
  toast.error(message, { id, description, duration: 4200 });

const STATUS_STYLES: Record<string, string> = {
  DRAFT: "bg-indigo-100 text-indigo-800 border-indigo-200",
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REIMBURSED: "bg-sky-100 text-sky-800 border-sky-200",
  PAID: "bg-teal-100 text-teal-800 border-teal-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
  WITHDRAWN: "bg-gray-100 text-gray-600 border-gray-200",
};

// An owner may edit/delete their report only before it is approved/settled.
// Mirrors EXPENSE_EDITABLE_STATUSES on the backend (DRAFT included — editing a
// draft submits it for approval).
const EDITABLE_STATUSES = new Set(["DRAFT", "PENDING", "REJECTED", "WITHDRAWN"]);

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={`${STATUS_STYLES[status] || ""} font-semibold`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </Badge>
  );
}

function SkBadge({ report }: { report: ExpenseReport }) {
  if (!["APPROVED", "REIMBURSED", "PAID"].includes(report.status)) return null;
  const s = report.sk_push_status;
  if (s === "SUCCESS")
    return <span className="text-xs text-emerald-700">✓ Synced to Khata</span>;
  if (s === "FAILED")
    return <span className="text-xs text-rose-700" title={report.sk_push_error || ""}>Sync failed</span>;
  return <span className="text-xs text-gray-400">Not synced</span>;
}

// How the reimbursement was paid, under the status. Read off the report, so it
// shows the same on the admin's list and on the employee's own "My Expenses"
// row — the thing the employee actually waits for.
function PaymentNote({ report }: { report: ExpenseReport }) {
  const payment = report.payment;
  if (!payment) return null;
  if (payment.status === "RETURNED") {
    return (
      <span
        className="text-xs text-rose-700"
        title={payment.return_reason || "The bank returned this transfer"}
      >
        Transfer returned
      </span>
    );
  }
  const detail = [fmtDate(payment.txn_date), payment.utr].filter(Boolean).join(" · ");
  return (
    <span className="text-xs text-emerald-700" title={payment.narration || ""}>
      Paid{detail ? ` · ${detail}` : ""}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* New report dialog                                                  */
/* ------------------------------------------------------------------ */

// GST rate options (mirror StartupKhata's expense form).
const GST_RATES = [0, 0.1, 0.25, 1, 3, 5, 12, 18, 28, 40];
const VENDOR_NONE = "__none__";
// Sentinel for the "+ Add new…" item in the Nature of Expense dropdown.
const CAT_ADD = "__add__";

interface LineDraft {
  category: string;
  amount: string;          // base when Tax Details is off (== line total)
  taxable_amount: string;  // base when Tax Details is on
  tax_rate: string;        // GST %
  cess_percent: string;
  description: string;
  receipt_url: string;
  receipt_filename: string;
}

const blankLine = (): LineDraft => ({
  category: "",
  amount: "",
  taxable_amount: "",
  tax_rate: "0",
  cess_percent: "",
  description: "",
  receipt_url: "",
  receipt_filename: "",
});

// Reverse of the submit mapping — rehydrate a saved line item into an editable
// draft so the dialog can pre-fill it when editing.
const lineFromItem = (it: ExpenseLineItem): LineDraft => ({
  category: it.category || "",
  amount: it.amount != null ? String(it.amount) : "",
  taxable_amount: it.taxable_amount != null ? String(it.taxable_amount) : "",
  tax_rate: it.tax_rate != null ? String(it.tax_rate) : "0",
  cess_percent: it.cess_percent != null ? String(it.cess_percent) : "",
  description: it.description || "",
  receipt_url: it.receipt_url || "",
  receipt_filename: it.receipt_filename || "",
});

interface LineTax {
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess_amount: number;
  total: number;
}

// Frontend mirror of utils.expense_service.compute_line_tax (the backend is the
// source of truth; this is for live UI only).
function lineTax(
  line: LineDraft,
  opts: { taxDetails: boolean; rcm: boolean; cess: boolean; sameState: boolean }
): LineTax {
  const taxable = opts.taxDetails
    ? parseFloat(line.taxable_amount) || 0
    : parseFloat(line.amount) || 0;
  const rate = opts.taxDetails ? parseFloat(line.tax_rate) || 0 : 0;
  const cessPct =
    opts.taxDetails && opts.cess && !opts.rcm ? parseFloat(line.cess_percent) || 0 : 0;
  const tax = (taxable * rate) / 100;
  let cgst = 0,
    sgst = 0,
    igst = 0;
  if (opts.sameState) {
    cgst = tax / 2;
    sgst = tax / 2;
  } else {
    igst = tax;
  }
  const cess_amount = (taxable * cessPct) / 100;
  const total = opts.rcm ? taxable : taxable + cgst + sgst + igst + cess_amount;
  return { taxable, cgst, sgst, igst, cess_amount, total };
}

function NewReportDialog({
  open,
  onOpenChange,
  categories,
  onCategoriesChange,
  onCreated,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  categories: string[];
  onCategoriesChange: (next: string[]) => void;
  onCreated: () => void;
  editing?: ExpenseReport | null;
}) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState("");
  const [expenseDate, setExpenseDate] = useState(todayISO());
  const [vendorID, setVendorID] = useState("");
  const [clientID, setClientID] = useState("");
  const [taxDetails, setTaxDetails] = useState(false);
  const [rcm, setRcm] = useState(false);
  const [cess, setCess] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [submitting, setSubmitting] = useState(false);

  // Inline "+ Add new…" Nature of Expense, scoped to the line that triggered it.
  const [addingForLine, setAddingForLine] = useState<number | null>(null);
  const [newCat, setNewCat] = useState("");
  const [savingCat, setSavingCat] = useState(false);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [companyState, setCompanyState] = useState("");

  // Load the party lists + the org's own state (for the GST split) on open.
  useEffect(() => {
    if (!open) return;
    vendorsApi
      .list({ page_size: 200 })
      .then((r) => setVendors(r.data?.items || []))
      .catch(() => setVendors([]));
    clientsApi
      .list({ page_size: 200 })
      .then((r) => setClients(r.data?.items || []))
      .catch(() => setClients([]));
    getCompany()
      .then((d) => setCompanyState(d?.companies?.[0]?.state || ""))
      .catch(() => setCompanyState(""));
  }, [open]);

  // Prefill the form on open: from the report being edited, or a clean slate
  // (today's date) for a new report. Owns the date so it never goes stale.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title || "");
      setNote(editing.note || "");
      setSupplierInvoiceNo(editing.supplierInvoiceNo || "");
      setExpenseDate(editing.expenseVoucherDate || todayISO());
      setVendorID(editing.vendorID || "");
      setClientID(editing.clientID || "");
      setTaxDetails(!!editing.tax_details);
      setRcm(!!editing.rcm);
      setCess(!!editing.cess);
      setAlreadyPaid(!!editing.already_paid);
      setLines(editing.items?.length ? editing.items.map(lineFromItem) : [blankLine()]);
    } else {
      setTitle("");
      setNote("");
      setSupplierInvoiceNo("");
      setExpenseDate(todayISO());
      setVendorID("");
      setClientID("");
      setTaxDetails(false);
      setRcm(false);
      setCess(false);
      setAlreadyPaid(false);
      setLines([blankLine()]);
    }
  }, [open, editing]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === vendorID),
    [vendors, vendorID]
  );
  const vendorState = selectedVendor?.state || "";
  // Missing either side ⇒ intra-state (CGST+SGST), matching the backend.
  const sameState = useMemo(() => {
    const norm = (s?: string | null) => String(s || "").trim().toLowerCase();
    if (!companyState || !vendorState) return true;
    return norm(companyState) === norm(vendorState);
  }, [companyState, vendorState]);

  const reset = () => {
    setTitle("");
    setNote("");
    setSupplierInvoiceNo("");
    setExpenseDate(todayISO());
    setVendorID("");
    setClientID("");
    setTaxDetails(false);
    setRcm(false);
    setCess(false);
    setAlreadyPaid(false);
    setLines([blankLine()]);
    setAddingForLine(null);
    setNewCat("");
  };

  // Add an org-wide Nature of Expense, then select it on the triggering line.
  const saveCat = async (i: number) => {
    const name = newCat.trim();
    if (!name) return;
    setSavingCat(true);
    const toastId = toast.loading("Adding nature of expense...");
    try {
      const next = await addExpenseCategory(name);
      onCategoriesChange(next);
      updateLine(i, { category: name });
      setAddingForLine(null);
      setNewCat("");
      notifySuccess("Nature of expense added", `${name} is selected on this line.`, toastId);
    } catch (e: any) {
      notifyError("Could not add nature of expense", errMsg(e, "Could not add"), toastId);
    } finally {
      setSavingCat(false);
    }
  };

  const updateLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (i: number) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // RCM forces Tax Details on + Cess off; Cess only valid with Tax on and no RCM.
  const onToggleTax = (v: boolean) => {
    setTaxDetails(v);
    if (!v) {
      setRcm(false);
      setCess(false);
    }
  };
  const onToggleRcm = (v: boolean) => {
    setRcm(v);
    if (v) {
      setTaxDetails(true);
      setCess(false);
    }
  };

  const handleReceipt = async (i: number, file?: File) => {
    if (!file) return;
    const toastId = toast.loading("Uploading receipt...");
    try {
      const { receipt_url, receipt_filename } = await uploadReceipt(file);
      updateLine(i, { receipt_url, receipt_filename });
      notifySuccess("Receipt attached", receipt_filename || file.name, toastId);
    } catch {
      notifyError("Receipt upload failed", "Try again or choose another file.", toastId);
    }
  };

  const computed = useMemo(
    () => lines.map((l) => lineTax(l, { taxDetails, rcm, cess, sameState })),
    [lines, taxDetails, rcm, cess, sameState]
  );
  const total = useMemo(() => computed.reduce((s, c) => s + c.total, 0), [computed]);
  // The ₹500 receipt rule is per line: a line needs its OWN receipt only when
  // that line's total exceeds the threshold. The report total never triggers it.
  const lineNeedsReceipt = (i: number) => computed[i].total > RECEIPT_THRESHOLD;
  const missingReceipt = lines.some((l, i) => lineNeedsReceipt(i) && !l.receipt_url);
  const lineBase = (l: LineDraft) =>
    taxDetails ? parseFloat(l.taxable_amount) || 0 : parseFloat(l.amount) || 0;
  const linesValid = lines.every((l) => l.category && lineBase(l) > 0);
  const canSubmit =
    !!title.trim() &&
    lines.length > 0 &&
    linesValid &&
    !missingReceipt &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const toastId = toast.loading(editing ? "Saving expense..." : "Submitting expense...");
    try {
      const payload = {
        title: title.trim(),
        note: note.trim() || undefined,
        supplierName: selectedVendor?.clientName || undefined,
        supplierInvoiceNo: supplierInvoiceNo.trim() || undefined,
        expenseVoucherDate: expenseDate || null,
        vendorID: vendorID || null,
        clientID: clientID || null,
        tax_details: taxDetails,
        rcm,
        cess,
        already_paid: alreadyPaid,
        items: lines.map((l, i) => ({
          category: l.category,
          amount: computed[i].total,
          taxable_amount: lineBase(l),
          tax_rate: taxDetails ? parseFloat(l.tax_rate) || 0 : 0,
          cess_percent: taxDetails && cess && !rcm ? parseFloat(l.cess_percent) || 0 : 0,
          spend_date: expenseDate || null,
          description: l.description || null,
          receipt_url: l.receipt_url || null,
          receipt_filename: l.receipt_filename || null,
        })),
      };
      if (editing) {
        await updateExpense(editing.id, payload);
        notifySuccess("Expense updated", "The report is back in the approval flow.", toastId);
      } else {
        await createExpense(payload);
        notifySuccess("Expense submitted", "Your approver can review it now.", toastId);
      }
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      notifyError(
        editing ? "Could not update expense" : "Could not submit expense",
        errMsg(e, editing ? "Could not update expense" : "Could not submit expense"),
        toastId
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 gap-3">
        <DialogHeader className="space-y-1">
          <DialogTitle>{editing ? "Edit Expense" : "New Expense"}</DialogTitle>
          <DialogDescription className="text-[13px]">
            Add one or more expense lines. A receipt is required for any single
            line over {fmtMoney(RECEIPT_THRESHOLD)}.
          </DialogDescription>
        </DialogHeader>

        <motion.div
          className="grid gap-2.5"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.33, 1, 0.68, 1] }}
        >
          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]"
            data-testid="expense-title-date-row"
          >
            <div className="grid gap-1.5" data-expense-field="title">
              <Label htmlFor="expense-title">Title</Label>
              <Input
                id="expense-title"
                placeholder="e.g. Client trip — Mumbai"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5" data-expense-field="date">
              <Label>Expense Date</Label>
              <DateField
                value={expenseDate}
                onChange={setExpenseDate}
                placeholder="Select date"
              />
            </div>
          </div>

          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_13rem]"
            data-testid="expense-party-row"
          >
            <div className="grid gap-1.5" data-expense-field="client">
              <Label>Client (optional)</Label>
              <Select
                value={clientID || VENDOR_NONE}
                onValueChange={(v) => setClientID(v === VENDOR_NONE ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value={VENDOR_NONE}>— None —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.clientName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5" data-expense-field="vendor">
              <Label>Vendor (optional)</Label>
              <Select
                value={vendorID || VENDOR_NONE}
                onValueChange={(v) => setVendorID(v === VENDOR_NONE ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value={VENDOR_NONE}>— None —</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.clientName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5" data-expense-field="invoice">
              <Label htmlFor="expense-invoice-no">Invoice No.</Label>
              <Input
                id="expense-invoice-no"
                value={supplierInvoiceNo}
                onChange={(e) => setSupplierInvoiceNo(e.target.value)}
              />
            </div>
          </div>

          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="expense-flags-line-header-row"
          >
            <Label
              data-testid="expense-line-items-heading"
              data-control-section="line-items-heading"
            >
              Line items
            </Label>
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid="expense-flags-row"
              data-compact-flags="true"
              data-flag-size="xs"
              data-control-section="flags"
            >
              <label
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="tax-details"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={taxDetails}
                  onCheckedChange={onToggleTax}
                />{" "}
                Tax Details
              </label>
              <label
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="rcm"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={rcm}
                  onCheckedChange={onToggleRcm}
                />{" "}
                RCM
              </label>
              <label
                className={`inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold ${
                  !taxDetails || rcm ? "opacity-50" : ""
                }`}
                data-expense-field="cess"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={cess}
                  onCheckedChange={setCess}
                  disabled={!taxDetails || rcm}
                />{" "}
                Cess
              </label>
              <div
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="already-paid"
              >
                <Switch
                  id="expense-already-paid"
                  className="-mx-1 scale-[0.72]"
                  checked={alreadyPaid}
                  onCheckedChange={setAlreadyPaid}
                />
                <Label htmlFor="expense-already-paid" className="cursor-pointer text-[11px]">
                  Already paid
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Already paid info"
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition hover:border-gray-300 hover:text-gray-900"
                    >
                      <Info size={10} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 text-sm leading-6">
                    I've already paid this, so no reimbursement is owed. It still
                    needs approval and settles as <b>Paid</b>.
                  </PopoverContent>
                </Popover>
              </div>
              {taxDetails && (
                <span className="text-xs text-gray-500">
                  {vendorID
                    ? sameState
                      ? "Intra-state — CGST + SGST"
                      : "Inter-state — IGST"
                    : "Pick a vendor to set place of supply (defaulting to intra-state)"}
                </span>
              )}
            </div>

            <button
              className="ml-auto inline-flex h-7 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-950 transition hover:bg-emerald-100"
              type="button"
              data-size="xs"
              data-control-section="add-line"
              onClick={addLine}
            >
              <Plus size={12} /> Add line
            </button>
          </div>

          <div className="grid gap-3">
            <div
              className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              data-testid="expense-lines-table"
              data-single-line-items-box="true"
            >
              <div className="hidden border-b border-gray-100 bg-gray-50/60 px-3 py-1.5 text-xs font-medium text-gray-500 lg:grid lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_2.5rem_2.5rem] lg:gap-2">
                <span>#</span>
                <span>Nature of Expense</span>
                <span>Description</span>
                <span>Amount (₹)</span>
                <span>Receipt</span>
                <span />
              </div>
              <AnimatePresence initial={false}>
                {lines.map((line, i) => (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.16, ease: [0.33, 1, 0.68, 1] }}
                    className={`grid gap-2 px-3 py-2 ${i === 0 ? "" : "border-t border-gray-100"}`}
                  >
                  <div
                    className="grid gap-2 lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_2.5rem_2.5rem] lg:items-center"
                    data-testid={`expense-line-row-${i}`}
                  >
                    <div
                      className="flex h-9 items-center text-xs font-bold text-gray-500"
                      data-line-field="serial"
                    >
                      {i + 1}
                    </div>
                    <div data-line-field="nature">
                      <span className="sr-only">Nature of Expense</span>
                      <Select
                        value={line.category}
                        onValueChange={(v) => {
                          if (v === CAT_ADD) {
                            setAddingForLine(i);
                            setNewCat("");
                          } else {
                            updateLine(i, { category: v });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                          <SelectItem value={CAT_ADD} className="text-emerald-700 font-medium">
                            + Add new…
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div data-line-field="description">
                      <span className="sr-only">Description</span>
                      <Input
                        className="h-9"
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                      />
                    </div>
                    <div data-line-field="amount">
                      <span className="sr-only">
                        {taxDetails ? "Taxable amount" : "Amount"}
                      </span>
                      <Input
                        className="h-9"
                        type="number"
                        min="0"
                        value={taxDetails ? line.taxable_amount : line.amount}
                        onChange={(e) =>
                          updateLine(
                            i,
                            taxDetails
                              ? { taxable_amount: e.target.value }
                              : { amount: e.target.value }
                          )
                        }
                      />
                    </div>
                    <div data-line-field="receipt">
                      <span className="sr-only">Receipt</span>
                      <label
                        className="relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                        title={line.receipt_filename ? "Replace receipt" : "Attach receipt"}
                      >
                        <Paperclip size={15} />
                        <span className="sr-only">
                          {line.receipt_filename ? "Replace receipt" : "Attach receipt"}
                        </span>
                        {line.receipt_filename && (
                          <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                            1
                          </span>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleReceipt(i, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                      onClick={() => removeLine(i)}
                      title="Remove line"
                    >
                      <Trash2 size={14} />
                      <span className="sr-only">Remove line</span>
                    </button>
                  </div>

                  {taxDetails && (
                    <div className="grid gap-2 rounded-md bg-gray-50 px-2 py-1.5 sm:grid-cols-[6.5rem_6.5rem_minmax(0,1fr)] sm:items-end lg:ml-[2.75rem]">
                      <div className="grid gap-1">
                      <span className="text-[11px] text-gray-500">GST Rate</span>
                      <Select
                        value={line.tax_rate}
                        onValueChange={(v) => updateLine(i, { tax_rate: v })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map((r) => (
                            <SelectItem key={r} value={String(r)}>
                              {r}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                      {cess && !rcm && (
                        <div className="grid gap-1">
                          <span className="text-[11px] text-gray-500">Cess %</span>
                          <Input
                            className="h-8"
                            type="number"
                            min="0"
                            max="999"
                            value={line.cess_percent}
                            onChange={(e) => updateLine(i, { cess_percent: e.target.value })}
                          />
                        </div>
                      )}
                      {taxDetails && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 sm:justify-end">
                          {sameState ? (
                            <>
                              <span>CGST <b>{fmtMoney(computed[i].cgst)}</b></span>
                              <span>SGST <b>{fmtMoney(computed[i].sgst)}</b></span>
                            </>
                          ) : (
                            <span>IGST <b>{fmtMoney(computed[i].igst)}</b></span>
                          )}
                          {cess && !rcm && (
                            <span>Cess <b>{fmtMoney(computed[i].cess_amount)}</b></span>
                          )}
                          {rcm && (
                            <span className="text-amber-700">
                              Reverse charge — tax not added to payable
                            </span>
                          )}
                          <span>
                            Line total <b>{fmtMoney(computed[i].total)}</b>
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                {addingForLine === i && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-end gap-2 rounded-md bg-emerald-50/60 p-2 lg:ml-[2.75rem]"
                    >
                    <div className="grid gap-1 flex-1">
                      <span className="text-[11px] text-gray-500">New Nature of Expense</span>
                      <Input
                        className="h-9"
                        autoFocus
                        placeholder="e.g. Cloud Hosting"
                        value={newCat}
                        onChange={(e) => setNewCat(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveCat(i);
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="gt-btn is-primary is-sm"
                      disabled={savingCat || !newCat.trim()}
                      onClick={() => saveCat(i)}
                    >
                      {savingCat ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="gt-btn is-ghost is-sm"
                      onClick={() => {
                        setAddingForLine(null);
                        setNewCat("");
                      }}
                    >
                      Cancel
                    </button>
                    </motion.div>
                )}

                  {line.receipt_filename && (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 lg:ml-[2.75rem]">
                      <Check size={12} /> {line.receipt_filename}
                    </span>
                  )}
                  {lineNeedsReceipt(i) && !line.receipt_url && (
                    <span className="flex items-center gap-1 text-xs text-amber-700 lg:ml-[2.75rem]">
                      <AlertTriangle size={12} /> Receipt required — over{" "}
                      {fmtMoney(RECEIPT_THRESHOLD)}
                    </span>
                  )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem] md:items-end"
            data-testid="expense-note-total-row"
          >
            <div className="grid gap-1.5" data-summary-field="note">
              <Label>Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="min-h-[64px]"
              />
            </div>
            <div
              className="flex min-h-[64px] items-center justify-between rounded-lg bg-gray-50 px-4 py-3 md:flex-col md:items-start md:justify-center"
              data-summary-field="total"
            >
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-xl font-bold">{fmtMoney(total)}</span>
            </div>
          </div>

          {/* The commute offset is applied when the claim is saved, so show it
              here rather than letting the employee discover a smaller figure
              afterwards. Renders nothing unless the org configured it. */}
          <ConveyanceNotice
            spendDate={expenseDate || null}
            lines={lines.map((l, i) => ({
              category: l.category,
              amount: computed[i]?.total ?? 0,
            }))}
          />

          {missingReceipt && (
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle size={16} />
              Attach a receipt to each line over {fmtMoney(RECEIPT_THRESHOLD)}.
            </div>
          )}
        </motion.div>

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button className="gt-btn is-primary" disabled={!canSubmit} onClick={submit}>
            {submitting
              ? editing
                ? "Saving…"
                : "Submitting…"
              : editing
                ? "Save changes"
                : "Submit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Decision (approve/reject) dialog                                   */
/* ------------------------------------------------------------------ */

function DecisionDialog({
  state,
  onClose,
  onDone,
}: {
  state: { report: ExpenseReport; action: "approved" | "rejected" } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setNote(""), [state]);

  if (!state) return null;
  const { report, action } = state;

  const confirm = async () => {
    setBusy(true);
    const toastId = toast.loading(action === "approved" ? "Approving expense..." : "Rejecting expense...");
    try {
      await updateExpenseStatus(report.id, action, note.trim() || undefined);
      notifySuccess(`Expense ${action}`, report.title, toastId);
      onClose();
      onDone();
    } catch (e: any) {
      notifyError("Action failed", errMsg(e, "Action failed"), toastId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === "approved" ? "Approve" : "Reject"} — {report.title}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <PersonAvatar
              userId={report.user_id}
              employeeId={report.emptID}
              employeeCode={report.employeeID}
              name={report.name}
              size={24}
            />
            <span>
              {report.name} · {fmtMoney(report.total_amount)} · {report.items.length} item(s)
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Note (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </div>
        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={onClose}>Cancel</button>
          <button
            className={`gt-btn ${action === "approved" ? "is-primary" : "is-danger"}`}
            disabled={busy}
            onClick={confirm}
          >
            {busy ? "Saving…" : action === "approved" ? "Approve" : "Reject"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Detail dialog                                                      */
/* ------------------------------------------------------------------ */

// A read-only "input box" mirroring the create form's <Label> + <Input> pairs,
// so the View dialog looks like the New Expense popup with the saved values.
function ViewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <div className="min-h-9 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
        {children}
      </div>
    </div>
  );
}

// View dialog — the SAME layout as the New Expense create popup, but read-only,
// populated with the data saved at create time (Title, Client/Vendor, Invoice,
// each line's Nature/Description/Amount/Receipt, Note, Total).
function DetailDialog({ report, onClose }: { report: ExpenseReport | null; onClose: () => void }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  // Resolve client/vendor display names (the report stores ids; the vendor name
  // is also on supplierName). Loaded lazily when the dialog opens.
  useEffect(() => {
    if (!report) return;
    clientsApi.list({ page_size: 200 }).then((r) => setClients(r.data?.items || [])).catch(() => setClients([]));
    vendorsApi.list({ page_size: 200 }).then((r) => setVendors(r.data?.items || [])).catch(() => setVendors([]));
  }, [report]);

  if (!report) return null;

  const clientName = clients.find((c) => c.id === report.clientID)?.clientName || "—";
  const vendorName =
    report.supplierName || vendors.find((v) => v.id === report.vendorID)?.clientName || "—";
  const flags: [string, boolean][] = [
    ["Tax Details", report.tax_details],
    ["RCM", report.rcm],
    ["Cess", report.cess],
    ["Already paid", report.already_paid],
  ];

  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 gap-3">
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center gap-2">
            Expense Details <StatusBadge status={report.status} />
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-[13px]">
            <PersonAvatar
              userId={report.user_id}
              employeeId={report.emptID}
              employeeCode={report.employeeID}
              name={report.name}
              size={24}
            />
            <span>
              {report.name || "—"}
              {report.submitted_on ? ` · Submitted ${fmtDate(report.submitted_on)}` : ""}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2.5">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
            <ViewField label="Title">{report.title || "—"}</ViewField>
            <ViewField label="Expense Date">{fmtDate(report.expenseVoucherDate) || "—"}</ViewField>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_13rem]">
            <ViewField label="Client (optional)">{clientName}</ViewField>
            <ViewField label="Vendor (optional)">{vendorName}</ViewField>
            <ViewField label="Invoice No.">{report.supplierInvoiceNo || "—"}</ViewField>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Label>Line items</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {flags.map(([label, on]) => (
                <span
                  key={label}
                  className={`inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-semibold ${
                    on ? "bg-emerald-100 text-emerald-800" : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {on && <Check size={12} />} {label}
                </span>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="hidden border-b border-gray-100 bg-gray-50/60 px-3 py-1.5 text-xs font-medium text-gray-500 lg:grid lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_minmax(120px,0.6fr)] lg:gap-2">
              <span>#</span>
              <span>Nature of Expense</span>
              <span>Description</span>
              <span>Amount (₹)</span>
              <span>Receipt</span>
            </div>
            {report.items.map((it, i) => (
              <div
                key={it.id || i}
                className={`grid gap-2 px-3 py-2 lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_minmax(120px,0.6fr)] lg:items-center ${
                  i === 0 ? "" : "border-t border-gray-100"
                }`}
              >
                <div className="text-xs font-bold text-gray-500">{i + 1}</div>
                <div className="text-sm">{it.category || "—"}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line">{it.description || "—"}</div>
                <div className="text-sm font-medium">{fmtMoney(it.amount)}</div>
                <div className="text-sm">
                  {it.receipt_url ? (
                    <a
                      href={expenseReceiptHref(it.receipt_url)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                        e.preventDefault();
                        openExpenseReceipt(it.receipt_url);
                      }}
                      className="text-sky-700 inline-flex items-center gap-1"
                    >
                      <Paperclip size={12} /> {it.receipt_filename || "Receipt"}
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem] md:items-end">
            <div className="grid gap-1.5">
              <Label>Note (optional)</Label>
              <div className="min-h-[64px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-line">
                {report.note || "—"}
              </div>
            </div>
            <div className="flex min-h-[64px] items-center justify-between rounded-lg bg-gray-50 px-4 py-3 md:flex-col md:items-start md:justify-center">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-xl font-bold">{fmtMoney(report.total_amount)}</span>
            </div>
          </div>

          {report.decision_note && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Approver note:</span> {report.decision_note}
            </div>
          )}
          {/* The payout itself — what the employee opens this dialog to check.
              One transfer can settle several claims, so the reference repeats
              across them. */}
          {report.payment && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                report.payment.status === "RETURNED"
                  ? "border-rose-200 bg-rose-50 text-rose-800"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
            >
              {report.payment.status === "RETURNED" ? (
                <>
                  <span className="font-semibold">Transfer returned</span>
                  {report.payment.return_reason ? ` — ${report.payment.return_reason}.` : "."} This claim is
                  still approved and owed.
                </>
              ) : (
                <>
                  <span className="font-semibold">Reimbursement paid</span>
                  {report.payment.txn_date ? ` on ${fmtDate(report.payment.txn_date)}` : ""}
                  {report.payment.utr ? ` · Ref ${report.payment.utr}` : ""}
                  {report.payment.source === "statement" && report.payment.statement_name
                    ? ` · from ${report.payment.statement_name}`
                    : ""}
                </>
              )}
            </div>
          )}
          <SkBadge report={report} />
        </div>

        <DialogFooter>
          <button type="button" className="gt-btn is-ghost" onClick={onClose}>
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export function Expenses() {
  const { user } = useAuth();
  const { canManage, hasAccess } = useAccess();
  // Access Management decides, not the role: the "expenses" panel at Manage
  // unlocks the approval/reimbursement side (Pending Approvals + All tabs,
  // approve/reject, mark paid); at View a user only sees their own claims.
  // With NO Access profile assigned, canManage() answers a permissive `true` for
  // every panel — that's what keeps admins working (they have no Employee row,
  // so they never carry a profile). On Expenses that also handed a plain
  // employee the whole approval side: the All tab, the "All Employees" filter
  // and the decide buttons, none of which the server actually allows. So when
  // there's no profile, fall back to the role, exactly like the backend's
  // can(..., roles=…) fallback — manager tier and above, never an employee.
  const canApprove = canManage("expenses") && (hasAccess || isAdminRole(user?.role));
  const isElevated = canApprove;

  const [tab, setTab] = useState<ExpenseTab>("mine");
  // Default the range to "this month so far": 1st of the current month → today.
  // APPLIED filters — drive the results; only change when "Apply Filter" is clicked.
  const [fromDate, setFromDate] = useState(monthStartISO);
  const [toDate, setToDate] = useState(todayISO);
  const [empQuery, setEmpQuery] = useState("");          // filter: employee name
  const [natureFilter, setNatureFilter] = useState("");  // filter: nature of expense
  // DRAFT filters — bound to the inputs; committed to the applied state on Apply.
  const [dFrom, setDFrom] = useState(monthStartISO);
  const [dTo, setDTo] = useState(todayISO);
  const [dEmp, setDEmp] = useState("");
  const [dNature, setDNature] = useState("");
  const [page, setPage] = useState(1);                   // pagination (1-based)
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ExpenseReport | null>(null);
  const [deleting, setDeleting] = useState<ExpenseReport | null>(null);
  const [detail, setDetail] = useState<ExpenseReport | null>(null);
  const [decision, setDecision] = useState<{ report: ExpenseReport; action: "approved" | "rejected" } | null>(null);
  // Bulk selection for approving many pending expenses at once (admin side).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bank bulk-transfer export (HR/finance) — the expense twin of the payroll
  // "Export Excel" on Payroll Approval.
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Payment status from the bank statement — the other half of the export: the
  // sheet asks for the transfers, this records which of them went out.
  const [statementOpen, setStatementOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Drafts tab: own drafts for a regular user, org-wide for an admin.
      const { expenses } =
        tab === "drafts"
          ? await getExpenses(isElevated ? "all" : "mine", "DRAFT")
          : await getExpenses(tab);
      setReports(expenses);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [tab, isElevated]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getExpenseCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // Land on the first tab available for this role — All for admins, Pending
  // Approvals for approvers, else My Expenses. Runs once the role resolves
  // (user hydrates async) and never overrides a later manual tab switch.
  const didInitTab = useRef(false);
  useEffect(() => {
    if (didInitTab.current || !user) return;
    didInitTab.current = true;
    if (isElevated) setTab("all");
    else if (canApprove) setTab("pending");
  }, [user, isElevated, canApprove]);

  // Distinct "Nature of Expense" values present in the loaded reports (union of
  // every line item's category), for the nature dropdown.
  const natureOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of reports) {
      for (const it of r.items || []) {
        if (it.category) set.add(it.category);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [reports]);

  // Distinct employee names present, for the employee-name filter autocomplete.
  const empNameOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of reports) if (r.name) set.add(r.name);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [reports]);

  // Client-side filters: submitted-date range + employee name + nature of expense.
  // yyyy-MM-dd compares lexicographically = chronologically, so plain string
  // comparison is safe. Undated rows drop out once any date bound is set.
  const dateFilterActive = Boolean(fromDate || toDate);
  const empQ = empQuery.trim().toLowerCase();
  const filtersActive = dateFilterActive || Boolean(empQ) || Boolean(natureFilter);
  const filtered = useMemo(() => {
    return reports.filter((r) => {
      // Date range on the submitted date.
      if (dateFilterActive) {
        if (!r.submitted_on) return false;
        let d: string;
        try {
          d = format(new Date(r.submitted_on), "yyyy-MM-dd");
        } catch {
          return false;
        }
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
      }
      // Employee name (substring, case-insensitive).
      if (empQ && !(r.name || "").toLowerCase().includes(empQ)) return false;
      // Nature of expense — match when any line item carries the chosen category.
      if (natureFilter && !(r.items || []).some((it) => (it.category || "") === natureFilter)) {
        return false;
      }
      return true;
    });
  }, [reports, fromDate, toDate, dateFilterActive, empQ, natureFilter]);

  // ---- Pagination ----
  const PAGE_SIZE = 10;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Keep the page in range when the result set shrinks (e.g. after filtering).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  // Commit the draft inputs to the applied filters (and jump back to page 1).
  const applyFilters = () => {
    setFromDate(dFrom);
    setToDate(dTo);
    setEmpQuery(dEmp);
    setNatureFilter(dNature);
    setPage(1);
  };
  const hasPendingFilterChanges =
    dFrom !== fromDate ||
    dTo !== toDate ||
    dEmp !== empQuery ||
    dNature !== natureFilter;
  const clearFilters = () => {
    setDFrom("");
    setDTo("");
    setDEmp("");
    setDNature("");
    setFromDate("");
    setToDate("");
    setEmpQuery("");
    setNatureFilter("");
    setPage(1);
  };

  const stats = useMemo(() => {
    const pending = filtered.filter((r) => r.status === "PENDING");
    const approved = filtered.filter((r) => r.status === "APPROVED");
    const reimbursed = filtered.filter((r) => r.status === "REIMBURSED");
    const sum = (rs: ExpenseReport[]) => rs.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    return {
      pendingCount: pending.length,
      pendingAmount: sum(pending),
      approvedAmount: sum(approved),
      reimbursedAmount: sum(reimbursed),
    };
  }, [filtered]);

  // ---- Bank bulk-transfer export ----
  // Only APPROVED claims are payable: REIMBURSED is already paid, PAID was
  // settled by the filer/company, and nothing else has been cleared. The sheet
  // covers exactly what the current filters show, so the admin can export one
  // month / one employee at a time.
  const approvedForExport = useMemo(
    () => (canApprove ? filtered.filter((r) => r.status === "APPROVED") : []),
    [filtered, canApprove]
  );

  // The sheet has one row per EMPLOYEE for the total of their claims (the
  // backend groups them in utils/expense_payments.approved_payees), so the
  // count shown to the exporter is people, not claims.
  const payeeCountForExport = useMemo(
    () => new Set(approvedForExport.map((r) => r.emptID || `user:${r.user_id || ""}`)).size,
    [approvedForExport]
  );

  const runExport = useCallback(
    async (bank: string, fields: Record<string, string>) => {
      const ids = approvedForExport.map((r) => r.id);
      const payees = payeeCountForExport;
      if (ids.length === 0) {
        notifyError("Nothing to export", "No approved expenses in the current view.");
        return;
      }
      setExporting(true);
      const toastId = toast.loading("Building the bank upload sheet...");
      try {
        const blob = await exportExpenseTransferSheet(bank, fields, ids);
        const now = new Date();
        // Same name the backend puts in Content-Disposition (AU: fixed prefix +
        // upper-cased payment instructions; other banks: dated, descriptive).
        const fname = transferSheetFilename("Expense", bank, now.getFullYear(), now.getMonth() + 1, fields);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fname;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        notifySuccess(
          "Bank sheet exported",
          `${ids.length} approved expense${ids.length > 1 ? "s" : ""} → ${payees} transfer${
            payees === 1 ? "" : "s"
          } · ${fname}`,
          toastId
        );
      } catch (e: any) {
        notifyError("Export failed", errMsg(e, "Could not export expenses"), toastId);
      } finally {
        setExporting(false);
      }
    },
    [approvedForExport, payeeCountForExport]
  );

  // Show the Employee column when the list can span multiple people: the All
  // and Pending tabs, and the admin's org-wide Drafts view.
  const showEmp = tab === "all" || tab === "pending" || (tab === "drafts" && isElevated);
  // Row is the signed-in user's own (drafts tab can show others' when admin).
  const isOwnRow = (r: ExpenseReport) => !!user && r.user_id === (user as any).id;

  // Can the signed-in user act on this pending row? Someone else's claim: yes,
  // if they hold Expenses at Manage. Their OWN claim: only when it routed to the
  // HR-Admin fan-out — i.e. they have no manager above them, so the group it was
  // routed to is the one they belong to. A manager's own claim routes to THEIR
  // manager and stays theirs to decide. Mirrors can_decide_expense server-side.
  const canDecideRow = (r: ExpenseReport) =>
    canApprove &&
    r.status === "PENDING" &&
    (!isOwnRow(r) || r.approverID === HR_ADMIN_APPROVER);

  // ---- Bulk selection (approve many pending expenses at once) ----
  // Only pending rows in an approval tab are selectable; "My Expenses" and
  // "Drafts" have no bulk approve.
  const showSelect = (tab === "all" || tab === "pending") && canApprove;
  // Bulk-approve only offers rows this user can actually decide — an own claim
  // routed to a manager would 403 and fail the whole batch's row.
  const selectablePending = useMemo(
    () => (showSelect ? filtered.filter(canDecideRow) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, showSelect, canApprove, user]
  );
  // Count only rows that are BOTH selected and currently visible (filters can
  // hide a previously-selected row), so the toolbar count == what bulkApprove acts on.
  const selectedCount = selectablePending.filter((r) => selectedIds.has(r.id)).length;
  const allSelected = selectablePending.length > 0 && selectedCount === selectablePending.length;
  const someSelected = selectedCount > 0 && !allSelected;

  // Drop the selection + reset to page 1 whenever the tab changes.
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [tab]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      selectablePending.length > 0 && selectablePending.every((r) => prev.has(r.id))
        ? new Set<string>()
        : new Set(selectablePending.map((r) => r.id))
    );
  };

  const bulkApprove = async () => {
    const ids = selectablePending.filter((r) => selectedIds.has(r.id)).map((r) => r.id);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const toastId = toast.loading(`Approving ${ids.length} expense${ids.length > 1 ? "s" : ""}...`);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => updateExpenseStatus(id, "approved"))
      );
      const ok = results.filter((x) => x.status === "fulfilled").length;
      const failed = results.length - ok;
      if (failed === 0) {
        notifySuccess("Expenses approved", `${ok} approved`, toastId);
      } else {
        notifyError("Some approvals failed", `${ok} approved, ${failed} failed`, toastId);
      }
      setSelectedIds(new Set());
      load();
    } catch (e: any) {
      notifyError("Bulk approve failed", errMsg(e, "Bulk approve failed"), toastId);
    } finally {
      setBulkBusy(false);
    }
  };

  const doWithdraw = async (r: ExpenseReport) => {
    const toastId = toast.loading("Withdrawing expense...");
    try {
      await withdrawExpense(r.id);
      notifySuccess("Expense withdrawn", r.title, toastId);
      load();
    } catch (e: any) {
      notifyError("Could not withdraw", errMsg(e, "Could not withdraw"), toastId);
    }
  };

  const doSubmitDraft = async (r: ExpenseReport) => {
    const toastId = toast.loading("Submitting for approval...");
    try {
      await submitDraftExpense(r.id);
      notifySuccess("Submitted for approval", r.title, toastId);
      load();
    } catch (e: any) {
      notifyError("Could not submit", errMsg(e, "Could not submit"), toastId);
    }
  };

  const doReimburse = async (r: ExpenseReport) => {
    const toastId = toast.loading("Marking reimbursement...");
    try {
      await reimburseExpense(r.id);
      notifySuccess("Marked as reimbursed", r.title, toastId);
      load();
    } catch (e: any) {
      notifyError("Could not mark reimbursed", errMsg(e, "Could not mark reimbursed"), toastId);
    }
  };

  const doRetry = async (r: ExpenseReport) => {
    const toastId = toast.loading("Retrying StartupKhata push...");
    try {
      const res = await retryExpensePush(r.id);
      notifySuccess("StartupKhata push retried", res?.status || "Done", toastId);
      load();
    } catch (e: any) {
      notifyError("Retry failed", errMsg(e, "Retry failed"), toastId);
    }
  };

  const doDelete = async (r: ExpenseReport) => {
    const toastId = toast.loading("Deleting expense...");
    try {
      await deleteExpense(r.id);
      notifySuccess("Expense deleted", r.title, toastId);
      setDeleting(null);
      load();
    } catch (e: any) {
      notifyError("Could not delete", errMsg(e, "Could not delete"), toastId);
    }
  };

  const tabs: { key: ExpenseTab; label: string }[] = [
    ...(isElevated ? [{ key: "all" as ExpenseTab, label: "All" }] : []),
    ...(canApprove ? [{ key: "pending" as ExpenseTab, label: "Pending Approvals" }] : []),
    { key: "mine", label: "My Expenses" },
    // Drafts: auto-generated (e.g. asset repair) expenses awaiting the owner's
    // submission. Own drafts for a user; org-wide for an admin.
    { key: "drafts", label: "Drafts" },
  ];

  return (
    <div className="gt-themed p-6 space-y-6">
      <PageHeader
        title={<>Expense <em>Management</em></>}
        subtitle={<>Track, approve and reimburse <em>employee</em> expenses.</>}
        actions={
          <div className="flex items-center gap-2">
            {canApprove && (
              <button
                className="gt-btn is-ghost"
                onClick={() => setExportOpen(true)}
                disabled={exporting || approvedForExport.length === 0}
                title={
                  approvedForExport.length === 0
                    ? "No approved expenses in the current view"
                    : `Export ${approvedForExport.length} approved expense(s) as ${payeeCountForExport} bank transfer(s) — one row per employee`
                }
              >
                <Download size={16} /> Export Excel
              </button>
            )}
            {canApprove && (
              <button
                className="gt-btn is-ghost"
                onClick={() => setStatementOpen(true)}
                title="Match your bank statement to the approved claims and mark them reimbursed"
              >
                <Upload size={16} /> Upload Bank Statement
              </button>
            )}
            <button className="gt-btn is-primary" onClick={() => setCreateOpen(true)}>
              <Plus size={16} /> New Expense
            </button>
          </div>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="gt-stat">
          <div className="gt-stat-label"><Clock size={14} /> Pending</div>
          <div className="gt-stat-value">{stats.pendingCount}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><IndianRupee size={14} /> Pending value</div>
          <div className="gt-stat-value">{fmtMoney(stats.pendingAmount)}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><CheckCircle2 size={14} /> Approved</div>
          <div className="gt-stat-value">{fmtMoney(stats.approvedAmount)}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><Receipt size={14} /> Reimbursed</div>
          <div className="gt-stat-value">{fmtMoney(stats.reimbursedAmount)}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as ExpenseTab)}>
        <TabsList>
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          {/* Filters sit at the top of the card — the tab already names the view,
              so there's no duplicate "All"/"Pending Approvals" title. The row
              never wraps: each field FLEXES (flex-grow/shrink with min-width:0)
              so it enlarges on wide screens and shrinks on smaller screens or
              higher browser zoom (100% vs 125%) instead of overflowing. The
              action buttons keep their size (flex-shrink:0). */}
          <form
            className="flex flex-nowrap items-center gap-2 w-full"
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            {showEmp && (
              <div style={{ flex: "1 1 11rem", minWidth: 0 }}>
                <SearchableCombobox
                  value={dEmp || "All Employees"}
                  onChange={(v) => setDEmp(v === "All Employees" ? "" : v)}
                  options={["All Employees", ...empNameOptions]}
                  placeholder="All Employees"
                  searchPlaceholder="Search employees…"
                  emptyText="No data found."
                  matchTriggerWidth
                  className="h-auto px-3 py-1.5"
                />
              </div>
            )}
            <div style={{ flex: "1 1 12rem", minWidth: 0 }}>
              <SearchableCombobox
                value={dNature || "All natures of expense"}
                onChange={(v) => setDNature(v === "All natures of expense" ? "" : v)}
                options={["All natures of expense", ...natureOptions]}
                placeholder="All natures of expense"
                searchPlaceholder="Search natures…"
                emptyText="No data found."
                matchTriggerWidth
                className="h-auto px-3 py-1.5"
              />
            </div>
            <div style={{ flex: "1 1 9.5rem", minWidth: 0 }}>
              <DateField
                value={dFrom}
                onChange={setDFrom}
                placeholder="Start date"
                size="sm"
                className="w-full"
              />
            </div>
            <span className="text-gray-400 text-sm" style={{ flexShrink: 0 }}>–</span>
            <div style={{ flex: "1 1 9.5rem", minWidth: 0 }}>
              <DateField
                value={dTo}
                onChange={setDTo}
                placeholder="End date"
                size="sm"
                className="w-full"
              />
            </div>
            <button
              type="submit"
              disabled={!hasPendingFilterChanges}
              title={hasPendingFilterChanges ? "Apply filter changes" : "Change a filter to enable Apply"}
              className={`gt-btn is-primary is-sm transition-all ${
                hasPendingFilterChanges
                  ? "opacity-100 shadow-sm"
                  : "opacity-35 cursor-not-allowed"
              }`}
              style={{ flexShrink: 0 }}
            >
              Apply Filter
            </button>
            {filtersActive && (
              <button type="button" onClick={clearFilters} className="gt-btn is-ghost is-sm" style={{ flexShrink: 0 }}>
                Clear
              </button>
            )}
          </form>
        </CardHeader>
        <CardContent>
          {showSelect && selectablePending.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <button
                type="button"
                onClick={toggleSelectAll}
                className="gt-btn is-ghost is-sm"
              >
                {allSelected ? "Deselect all" : `Select all pending (${selectablePending.length})`}
              </button>
              {selectedCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {selectedCount} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={bulkBusy}
                    className="gt-btn is-ghost is-sm"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={bulkApprove}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    <Check size={14} />
                    {bulkBusy ? "Approving…" : `Approve selected (${selectedCount})`}
                  </button>
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="py-10 text-center text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-gray-400">
              {filtersActive
                ? "No expenses match these filters."
                : tab === "drafts"
                  ? "No draft expenses. Approved third-party asset repairs land here first — review, edit, then submit for approval."
                  : "No expenses here yet."}
            </div>
          ) : (
            <>
            <div className="max-h-[70vh] overflow-x-auto overflow-y-auto">
              {/* Explicit column widths keep every field visible at normal zoom.
                  Narrow viewports retain the same readable layout and can scroll
                  horizontally instead of squeezing columns out of view. */}
              <table className={`w-full text-sm table-fixed ${showEmp ? "min-w-[1160px]" : "min-w-[1010px]"}`}>
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    {showSelect && (
                      <th className="sticky top-0 z-10 bg-white py-2 pr-3 w-8">
                        <input
                          type="checkbox"
                          className="accent-emerald-600 cursor-pointer align-middle"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={toggleSelectAll}
                          aria-label="Select all pending"
                          title="Select all pending"
                        />
                      </th>
                    )}
                    <th className="sticky top-0 z-10 w-[210px] bg-white py-2 pr-3">Expense</th>
                    <th className="sticky top-0 z-10 w-[160px] whitespace-nowrap bg-white py-2 pr-3">Nature</th>
                    {showEmp && <th className="sticky top-0 z-10 w-[135px] bg-white py-2 pr-3">Employee</th>}
                    <th className="sticky top-0 z-10 w-14 bg-white py-2 pr-3">Items</th>
                    <th className="sticky top-0 z-10 w-24 bg-white py-2 pr-3">Total</th>
                    <th className="sticky top-0 z-10 w-32 bg-white py-2 pr-3">Status</th>
                    <th className="sticky top-0 z-10 w-[118px] whitespace-nowrap bg-white py-2 pr-3">Expense Date</th>
                    <th className="sticky top-0 z-10 w-[118px] whitespace-nowrap bg-white py-2 pr-3">Submitted</th>
                    <th className="sticky top-0 z-10 w-32 bg-white py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setDetail(r)}
                    >
                      {showSelect && (
                        <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>
                          {r.status === "PENDING" ? (
                            <input
                              type="checkbox"
                              className="accent-emerald-600 cursor-pointer align-middle"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleSelect(r.id)}
                              aria-label={`Select ${r.title}`}
                            />
                          ) : null}
                        </td>
                      )}
                      {/* Truncate + tooltip: the column is now capped, so a long
                          title would otherwise wrap the row to two/three lines. */}
                      <td className="py-3 pr-3 font-medium">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate" title={r.title}>{r.title}</span>
                          {r.source === "ASSET_REPAIR" && (
                            <span className="shrink-0 inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">
                              Asset repair
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Nature of Expense — the distinct line-item categories on
                          the report (usually one; joined when a report mixes a few).
                          Truncates with a tooltip so long values never collide. */}
                      <td className="py-3 pr-3 text-gray-600">
                        {(() => {
                          const cats = Array.from(
                            new Set((r.items || []).map((it) => it.category).filter(Boolean))
                          );
                          const label = cats.length ? cats.join(", ") : "—";
                          return <span className="block truncate" title={label}>{label}</span>;
                        })()}
                      </td>
                      {showEmp && (
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <PersonAvatar
                              userId={r.user_id}
                              employeeId={r.emptID}
                              employeeCode={r.employeeID}
                              name={r.name}
                              size={24}
                            />
                            <span className="block min-w-0 truncate" title={r.name || ""}>{r.name || "—"}</span>
                          </div>
                        </td>
                      )}
                      <td className="py-3 pr-3">{r.items.length}</td>
                      <td className="py-3 pr-3 font-semibold whitespace-nowrap">{fmtMoney(r.total_amount)}</td>
                      <td className="py-3 pr-3">
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={r.status} />
                          <PaymentNote report={r} />
                          <SkBadge report={r} />
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-gray-500 whitespace-nowrap">{fmtDate(r.expenseVoucherDate)}</td>
                      <td className="py-3 pr-3 text-gray-500 whitespace-nowrap">{fmtDate(r.submitted_on)}</td>
                      <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {/* Inline action icons by status. View opens by clicking
                              the row itself (not from here). */}
                          {(tab === "all" || tab === "pending") && canDecideRow(r) && (
                            <>
                              <button
                                type="button"
                                className="gt-btn is-ghost is-sm text-emerald-700"
                                title="Approve"
                                aria-label="Approve"
                                onClick={() => setDecision({ report: r, action: "approved" })}
                              >
                                <Check size={16} />
                              </button>
                              <button
                                type="button"
                                className="gt-btn is-ghost is-sm text-rose-600"
                                title="Reject"
                                aria-label="Reject"
                                onClick={() => setDecision({ report: r, action: "rejected" })}
                              >
                                <X size={16} />
                              </button>
                            </>
                          )}

                          {tab === "mine" && r.status === "PENDING" && (
                            <button
                              type="button"
                              className="gt-btn is-ghost is-sm"
                              title="Withdraw"
                              aria-label="Withdraw"
                              onClick={() => doWithdraw(r)}
                            >
                              <Undo2 size={16} />
                            </button>
                          )}

                          {/* Submit a draft for approval (→ PENDING) without
                              editing it — owner only, on the Drafts tab. */}
                          {tab === "drafts" && isOwnRow(r) && r.status === "DRAFT" && (
                            <button
                              type="button"
                              className="gt-btn is-primary is-sm"
                              title="Submit for approval"
                              onClick={() => doSubmitDraft(r)}
                            >
                              <Check size={14} /> Submit
                            </button>
                          )}

                          {/* Edit — the owner, on My Expenses or Drafts, for any
                              editable status (DRAFT included; editing a draft
                              submits it for approval). */}
                          {(tab === "mine" || tab === "drafts") &&
                            isOwnRow(r) &&
                            EDITABLE_STATUSES.has(r.status) && (
                              <button
                                type="button"
                                className="gt-btn is-ghost is-sm"
                                title="Edit"
                                aria-label="Edit"
                                onClick={() => setEditingReport(r)}
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                          {(tab === "mine" || tab === "drafts") &&
                            isOwnRow(r) &&
                            EDITABLE_STATUSES.has(r.status) && (
                              <button
                                type="button"
                                className="gt-btn is-ghost is-sm text-red-600"
                                title="Delete"
                                aria-label="Delete"
                                onClick={() => setDeleting(r)}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}

                          {isElevated && r.status === "APPROVED" && (
                            <button
                              type="button"
                              className="gt-btn is-ghost is-sm"
                              title="Mark reimbursed"
                              aria-label="Mark reimbursed"
                              onClick={() => doReimburse(r)}
                            >
                              <Receipt size={16} />
                            </button>
                          )}

                          {isElevated &&
                            (r.status === "APPROVED" || r.status === "REIMBURSED") &&
                            r.sk_push_status === "FAILED" && (
                              <button
                                type="button"
                                className="gt-btn is-ghost is-sm"
                                title="Retry sync"
                                aria-label="Retry sync"
                                onClick={() => doRetry(r)}
                              >
                                <RefreshCw size={16} />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 text-sm text-gray-600">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
                {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="gt-btn is-ghost is-sm"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <span className="font-medium">
                  Page {page} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page >= pageCount}
                  className="gt-btn is-ghost is-sm"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <NewReportDialog
        open={createOpen || !!editingReport}
        onOpenChange={(v) => {
          if (!v) {
            setCreateOpen(false);
            setEditingReport(null);
          } else {
            setCreateOpen(true);
          }
        }}
        editing={editingReport}
        categories={categories}
        onCategoriesChange={setCategories}
        onCreated={load}
      />
      <DecisionDialog state={decision} onClose={() => setDecision(null)} onDone={load} />
      <DetailDialog report={detail} onClose={() => setDetail(null)} />
      {/* Bank bulk-transfer sheet for approved claims — same per-bank layouts
          and dialog as the payroll export (components/BankExportDialog). */}
      <BankExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        onConfirm={runExport}
        exporting={exporting}
        title="Export Expense Bulk Upload Sheet"
        description={`Pick a bank, then fill the fields below — each value is written to every row of that bank's upload sheet. ${approvedForExport.length} approved expense${
          approvedForExport.length === 1 ? "" : "s"
        } will be included as ${payeeCountForExport} transfer${
          payeeCountForExport === 1 ? "" : "s"
        } — one row per employee, for the total they are owed.`}
      />
      {/* Payment status from the statement. One debit settles an employee's whole
          batch of claims — exactly how the sheet above exported them — and the
          reports it marks REIMBURSED are the same rows the employee sees under
          "My Expenses", so their panel updates with this. */}
      <ExpenseStatementUploadDialog
        open={statementOpen}
        onOpenChange={setStatementOpen}
        onApplied={(result) => {
          const claims = result.claims || 0;
          if (result.paid) {
            notifySuccess(
              "Reimbursements recorded",
              `${result.paid} employee${result.paid === 1 ? "" : "s"} paid · ${claims} claim${
                claims === 1 ? "" : "s"
              } marked reimbursed${result.returned ? ` · ${result.returned} returned` : ""}`
            );
          } else if (result.returned) {
            notifySuccess(
              "Returned transfers recorded",
              `${result.returned} transfer${result.returned === 1 ? "" : "s"} came back; those claims stay approved`
            );
          }
          load();
        }}
      />
      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete expense?</DialogTitle>
            <DialogDescription>
              "{deleting?.title}" ({fmtMoney(deleting?.total_amount ?? 0)}) will be
              removed. This can't be undone from here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              className="gt-btn is-danger"
              onClick={() => deleting && doDelete(deleting)}
            >
              <Trash2 size={14} /> Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Expenses;
